import { Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FormItemProps } from '../../../../../../hooks'
import * as Styled from '../../styles'
import { CompanyInfo, CompanyInfoSchema } from '../../utils'
import { AddressingLineFormat } from '../AddressingLineSelector/types/frAddressingLinesFormats'
import { EditableFieldGroups, ManualCompanyForm } from '../ManualCompanyForm'

interface CompanyMissingFieldsProps {
  missingFields: string[]
  addressingLineFormat: AddressingLineFormat
  formItem: <Name extends Path<CompanyInfo>>(
    props: Omit<FormItemProps<CompanyInfoSchema, Name>, 'error'>,
  ) => JSX.Element
}

export const CompanyMissingFields = ({
  formItem: FormItem,
  addressingLineFormat,
  missingFields,
}: CompanyMissingFieldsProps): ReactElement => {
  const { t } = useTranslation()

  const missingFieldGroups = useMemo<EditableFieldGroups[] | null>(() => {
    const missingGroups: EditableFieldGroups[] = []

    if (missingFields.includes('accountHolderFirstName') || missingFields.includes('accountHolderLastName')) {
      missingGroups.push('accountHolderName')
    }

    if (
      missingFields.includes('companyAddress') ||
      missingFields.includes('companyZipCode') ||
      missingFields.includes('companyCity')
    ) {
      missingGroups.push('address')
    }

    if (missingFields.includes('companyName')) {
      missingGroups.push('companyName')
    }

    return missingGroups
  }, [missingFields])

  return (
    <Flex flexDirection="column" gap={Spacing.S8}>
      <Text weight="bold" colorVariant="secondary">
        {t('e_invoice.kyc.step.company_details.required_information_missing.label').toUpperCase()}
      </Text>
      <Styled.InformationCard>
        <Text variant="bodySM">{t('e_invoice.kyc.step.company_details.required_information_missing.details')}</Text>
        <Flex flexDirection="column" gap={Spacing.S16}>
          <ManualCompanyForm
            addressingLineFormat={addressingLineFormat}
            fields={missingFieldGroups}
            formItem={FormItem}
          />
        </Flex>
      </Styled.InformationCard>
    </Flex>
  )
}
